import { lazy, useEffect } from 'react';

import { useFlow } from '@sunflower-games/flow-react';
import { observer } from 'mobx-react';
import { useSearchParams } from 'react-router-dom';

import { useBingoWindowWidthRedirect } from '../components/Bingo/hooks/useBingoWindowWidthRedirect';
import { ClaimRewardsAnimation } from '../components/ClaimRewardsAnimation/ClaimRewardsAnimation';
import { MetagamesAssetsPreloader } from '../components/Dialogs/MetagamesDialog/MetagamesAssetsPreloader';
import { FlyingCoinsAnimationComponent } from '../components/FlyingCoins/FlyingCoinsAnimationComponent';
import { Footer } from '../components/Footer/Footer';
import FooterNav from '../components/FooterNav/FooterNav';
import HomeHeader from '../components/headers/HomeHeader';
import HomeHeaderOldSearch from '../components/headers/HomeHeaderOldSearch';
import { Loader } from '../components/Loader/Loader';
import SideMenu from '../components/SideMenu/SideMenu';
import { PRIMARY_HTML_ELEMENT_ID } from '../config/constants';
import { MetaGamesEventType } from '../dto/metagames.types';
import { useAuthFlow } from '../features/auth/useAuthFlow';
import { BingoFTUEView } from '../features/onboarding/bingoFTUE/BingoFTUEView';
import { InteractiveAnimationFTUEView } from '../features/onboarding/interactiveAnimationFTUE/InteractiveAnimationFTUEView';
import { useProcessPaymentRedirect } from '../hooks/useProcessPaymentRedirect';
import { useSlotGameDeepLink } from '../hooks/useSlotGameDeepLink';
import { useSlotGameErrorHandler } from '../hooks/useSlotGameErrorHandler';
import { auth } from '../services/auth';
import { useFlag } from '../services/launchDarkly';
import { lifecycleEventEmitterService } from '../services/lifecycleEventEmitter/LifecycleEventEmitterService';
import { LandingKey } from '../stores/auth/types';
import { useFeatureManager } from '../stores/feature-manager/useFeatureManager';
import { EnumOpenDialog } from '../stores/ui/types';
import { useStores } from '../stores/useStores';
import { getParam } from '../utils/marketingHelper';

import DisconnectedByMultisession from './DisconnectedByMultisession';
import Home from './Home/Home';
import HomeOld from './HomeOldSearch/Home';

const Landing = lazy(() => import('./Landing/Landing'));
const Dialogs = lazy(() => import('../components/Dialogs/Dialogs'));
const FullPageDialogs = lazy(() => import('../components/FullPageDialogs/FullPageDialogs'));

const Lobby = observer(() => {
  const { authStore, userStore, bingoStore, uiStore } = useStores();

  const preventMultisession = useFlag('prevent_multisession_enable', false);
  const isOldSearchMode = useFlag('enable_old_search_mode', false);

  const isBingoDialogOpen =
    uiStore.openDialog.dialog === EnumOpenDialog.isOpenMetagamesDialog &&
    uiStore.openDialog.options?.type === MetaGamesEventType.BINGO;

  const { sendEvent } = useFlow();

  useAuthFlow();
  useProcessPaymentRedirect();
  useSlotGameErrorHandler();
  useFeatureManager();
  useBingoWindowWidthRedirect();

  const { isAuthenticationFlowDone, authorized, isDisconnectedByMultisession, isAuthWizardOpen } = authStore;
  const isAuthenticatedAndAllowed = authorized && auth.accessAllowed !== false;

  useSlotGameDeepLink(isAuthenticationFlowDone && isAuthenticatedAndAllowed && !!userStore.profile);

  const landingSearchParam = useSearchParams()[0].get('landing');
  const isDirectSignUpLanding = !!landingSearchParam?.startsWith(LandingKey.DIRECT_SU);
  const isDirectEmailLanding = landingSearchParam === LandingKey.DIRECT_EM;
  const isDirectRegLanding = landingSearchParam === LandingKey.DIRECT_REG;

  useEffect(() => {
    if (userStore.profile) {
      const urlParams = new URLSearchParams(window.location.search);
      sendEvent('app/user.enter_lobby', userStore.profile.firebaseUid, {
        url: window.location.href,
        utm_promo: getParam(urlParams, 'utm_promo') || undefined,
        utm_feature: getParam(urlParams, 'utm_feature') || undefined,
      });
    }
  }, [sendEvent, userStore.profile]);

  useEffect(() => {
    if (isAuthenticationFlowDone && !userStore.loading) {
      lifecycleEventEmitterService.emitMessage('ON_APP_LOBBY_LOADED');
    }
  }, [isAuthenticationFlowDone, userStore.loading]);

  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    const el = document.getElementById(PRIMARY_HTML_ELEMENT_ID);
    if (el) el.scrollTop = 0;

    return () => {
      if ('scrollRestoration' in history) {
        history.scrollRestoration = 'auto';
      }
    };
  }, []);

  if (!isAuthenticationFlowDone) {
    return <Loader fullScreen />;
  }

  if (preventMultisession && isDisconnectedByMultisession) {
    return <DisconnectedByMultisession />;
  }

  if (isDirectRegLanding && !isAuthenticatedAndAllowed && !isAuthWizardOpen) {
    return <Loader fullScreen />;
  }

  if (!isDirectEmailLanding && !isDirectSignUpLanding && !isDirectRegLanding && !isAuthenticatedAndAllowed) {
    return <Landing />;
  }

  if (userStore.loading) {
    return <Loader fullScreen />;
  }

  return (
    <>
      {isAuthenticatedAndAllowed && !isBingoDialogOpen && (
        <>
          {bingoStore.isInsideBingoArea ? null : isOldSearchMode ? <HomeHeaderOldSearch /> : <HomeHeader />}
          <SideMenu />
          {isOldSearchMode ? <HomeOld /> : <Home />}
          <MetagamesAssetsPreloader />
          <FlyingCoinsAnimationComponent />
          <ClaimRewardsAnimation />
          {isOldSearchMode && <Footer />}
          <InteractiveAnimationFTUEView />
          <BingoFTUEView entryPoint={bingoStore.bingoFtueEntryPoint} />
        </>
      )}
      <Dialogs />
      <FullPageDialogs />
      {isAuthenticatedAndAllowed && <FooterNav />}
    </>
  );
});

export default Lobby;
